import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Home() {
  const { t } = useTranslation();

  // Hero tags ordered by demand (High → niche)
  const HERO_CATEGORY_ORDER = [
    "it", "ai", "marketing", "psychology", "digital_product_design",
    "finance", "economics", "law", "cybersecurity", "scientific_research",
    "media_production", "nutritionist", "architecture", "esports",
    "online_sales",
  ];
  const categories = HERO_CATEGORY_ORDER.map((gid) => ({
    key: gid,
    label: t(`subjectGroups.${gid}`),
  }));

  const features = [
    {
      icon: <SearchIcon />,
      title: t("home.feature1Title"),
      text: t("home.feature1Text"),
    },
    {
      icon: <RequestIcon />,
      title: t("home.feature2Title"),
      text: t("home.feature2Text"),
    },
    {
      icon: <ChatIcon />,
      title: t("home.feature3Title"),
      text: t("home.feature3Text"),
    },
  ];

  const steps = [
    { num: "01", title: t("home.step1Title"), text: t("home.step1Text") },
    { num: "02", title: t("home.step2Title"), text: t("home.step2Text") },
    { num: "03", title: t("home.step3Title"), text: t("home.step3Text") },
  ];

  const benefits = [
    { title: t("home.benefit1Title"), text: t("home.benefit1Text"), accent: "#98dea3" },
    { title: t("home.benefit2Title"), text: t("home.benefit2Text"), accent: "#7269e3" },
  ];

  const philosophyItems = [
    t("home.philosophyItem1"),
    t("home.philosophyItem2"),
    t("home.philosophyItem3"),
    t("home.philosophyItem4"),
  ];

  return (
    <main className="min-h-screen bg-[#272c39] text-white">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-120px] top-[-120px] h-[320px] w-[320px] rounded-full bg-[#7269e3]/18 blur-3xl" />
          <div className="absolute right-[-100px] top-[80px] h-[280px] w-[280px] rounded-full bg-[#98dea3]/10 blur-3xl" />
          <div className="absolute left-[40%] bottom-[-120px] h-[260px] w-[260px] rounded-full bg-[#a783a6]/12 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-16 lg:py-24">
          <div className="grid min-w-0 items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="max-w-3xl min-w-0">
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/70 backdrop-blur sm:px-4 sm:py-2 sm:text-sm">
                {t("home.heroBadge")}
              </div>

              <h1 className="mt-4 break-words font-unbounded text-3xl font-extrabold leading-tight sm:text-4xl md:text-5xl lg:text-6xl">
                {t("home.heroLine1")}
                <span className="block">{t("home.heroLine2")}</span>
                <span className="block text-[#98dea3]">{t("home.heroLine3")}</span>
              </h1>

              <p className="mt-4 max-w-2xl break-words text-base leading-relaxed text-white/76 sm:mt-6 md:text-xl">
                {t("home.heroSubtitle")}
              </p>

              <div className="mt-6 flex flex-wrap gap-3 sm:mt-8 sm:gap-4">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-2xl bg-[#7269e3] px-5 py-3 font-semibold text-white transition hover:opacity-90 sm:px-6"
                >
                  {t("home.startNow")}
                </Link>

                <Link
                  to="/search"
                  className="inline-flex items-center justify-center rounded-2xl border border-white/12 bg-white/[0.05] px-5 py-3 font-semibold text-white transition hover:bg-white/[0.08] sm:px-6"
                >
                  {t("home.findSpecialist")}
                </Link>
              </div>

              {/* Category chips — wrap on all screen sizes so every category
                  is visible at once (the old horizontal scroller hid most of
                  them offscreen on phones with no visible scrollbar) */}
              <div className="mt-6 flex flex-wrap gap-2 sm:mt-8 sm:gap-3">
                {categories.map(({ key, label }) => (
                  <Link
                    key={key}
                    to={`/search?sphere=${key}`}
                    className="shrink-0 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-sm text-white/72 transition hover:border-white/20 hover:bg-white/[0.08] hover:text-white/90 sm:px-4 sm:py-2"
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            {/* iPhone Mockup — hidden on mobile */}
            <div className="relative hidden lg:block flex justify-center">
              {/* Glow */}
              <div className="absolute -inset-8 rounded-[60px] bg-[#7269e3]/12 blur-3xl pointer-events-none" />

              {/* iPhone outer frame */}
              <div className="relative mx-auto w-[300px] rounded-[52px] border-[3px] border-white/15 bg-[#161a26] p-[3px] shadow-[0_40px_100px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.08)]">

                {/* Inner screen bezel */}
                <div className="relative rounded-[50px] bg-[#1a1f30] overflow-hidden">

                  {/* Dynamic Island */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 h-[30px] w-[100px] rounded-full bg-[#0d1018] flex items-center justify-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[#1a1f30]" />
                    <span className="h-[10px] w-[10px] rounded-full border border-white/8 bg-[#252a3a]" />
                  </div>

                  {/* Status bar — domain replaces clock on the left */}
                  <div className="flex items-center justify-between px-6 pt-3 pb-1">
                    <span className="text-[10px] font-semibold text-white/45">9:41</span>
                    <div className="w-[100px]" />{/* spacer for island */}
                    <div className="flex items-center gap-1">
                      <svg viewBox="0 0 24 24" className="h-3 w-3 text-white/50" fill="currentColor"><path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>
                      <svg viewBox="0 0 24 24" className="h-3 w-3 text-white/50" fill="currentColor"><rect x="1" y="6" width="22" height="13" rx="2"/><path d="M23 10v5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>
                    </div>
                  </div>

                  {/* App content — safe area below Dynamic Island */}
                  <div className="px-4 pb-5 pt-3">

                    {/* App header */}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-base font-bold text-white leading-tight">{t("home.demoTitle")}</h3>
                      </div>
                      <div className="flex items-center gap-1.5 rounded-xl bg-[#7269e3]/15 px-2.5 py-1 text-[10px] font-semibold text-[#c9c4ff] ring-1 ring-[#7269e3]/25">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#98dea3] opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#98dea3]" />
                        </span>
                        live
                      </div>
                    </div>

                    {/* Request card */}
                    <div className="rounded-2xl border border-white/10 bg-[#20253a] p-3 shadow-[0_4px_20px_rgba(0,0,0,0.4)]">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <svg viewBox="0 0 24 24" className="h-3 w-3 text-[#7269e3]" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <circle cx="11" cy="11" r="7"/><path d="M20 20l-3-3" strokeLinecap="round"/>
                        </svg>
                        <p className="text-[10px] font-semibold text-[#a89ef5] uppercase tracking-wider">{t("home.demoRequestLabel")}</p>
                      </div>
                      <p className="text-xs text-white/80 leading-relaxed">{t("home.demoRequestText")}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="flex items-center gap-0.5 rounded-full bg-[#7269e3]/15 px-2 py-0.5 text-[10px] text-[#c9c4ff]">
                          #{t("subjectGroups.marketing")}
                        </span>
                        <span className="rounded-full bg-white/6 px-2 py-0.5 text-[10px] text-white/55">
                          #{t("common.modeOnline")}
                        </span>
                        <span className="rounded-full bg-white/6 px-2 py-0.5 text-[10px] text-white/55">
                          #{t("common.budget")}
                        </span>
                      </div>
                    </div>

                    {/* Specialist cards */}
                    <div className="mt-2.5 space-y-2">
                      {[
                        { initials: t("home.demoSpecialist1Initials", { defaultValue: "АК" }), name: t("home.demoSpecialist1Name", { defaultValue: "Анна К." }), role: t("home.demoSpecialist1Role"), rate: t("home.demoSpecialist1Rate"), color: "#98dea3" },
                        { initials: "DM", name: "Danylo M.", role: t("home.demoSpecialist2Role"), rate: t("home.demoSpecialist2Rate"), color: "#7269e3" },
                        { initials: "SL", name: "Sofia L.",  role: t("home.demoSpecialist3Role"), rate: t("home.demoSpecialist3Rate"), color: "#a783a6" },
                      ].map((card) => (
                        <div
                          key={card.name}
                          className="group flex items-center justify-between rounded-xl border border-white/8 bg-[#20253a] px-3 py-2.5 shadow-[0_2px_10px_rgba(0,0,0,0.3)] transition hover:border-white/15 hover:bg-[#262c42]"
                        >
                          <div className="flex items-center gap-2.5">
                            <div
                              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold"
                              style={{ backgroundColor: `${card.color}25`, color: card.color, border: `1px solid ${card.color}35` }}
                            >
                              {card.initials}
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-white">{card.name}</p>
                              <p className="text-[10px] text-white/50">{card.role}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-medium text-white/60">{card.rate}</span>
                            <div
                              className="flex h-6 w-6 items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition"
                              style={{ backgroundColor: `${card.color}25`, color: card.color }}
                            >
                              <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Search input — iPhone keyboard area */}
                    <div className="mt-3 flex items-center gap-2 rounded-2xl border border-white/10 bg-[#20253a] px-3 py-2.5">
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-white/25" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="7"/><path d="M20 20l-3-3" strokeLinecap="round"/>
                      </svg>
                      <span className="flex-1 text-xs text-white/25">Знайти маркетолога...</span>
                      <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#7269e3]">
                        <svg viewBox="0 0 24 24" className="h-3 w-3 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>

                    {/* iPhone home indicator */}
                    <div className="mt-4 flex justify-center">
                      <div className="h-1 w-24 rounded-full bg-white/20" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-5 md:px-6">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/52 md:text-base">
            <span>{t("home.trust1")}</span>
            <span className="hidden md:inline text-white/20">•</span>
            <span>{t("home.trust2")}</span>
            <span className="hidden md:inline text-white/20">•</span>
            <span>{t("home.trust3")}</span>
            <span className="hidden md:inline text-white/20">•</span>
            <span>{t("home.trust4")}</span>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="max-w-2xl">
          <p className="text-sm uppercase tracking-[0.22em] text-[#98dea3]">
            {t("home.featuresBadge")}
          </p>
          <h2 className="mt-3 text-3xl font-bold leading-tight md:text-5xl">
            {t("home.featuresTitle")}
          </h2>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {features.map((item) => (
            <div
              key={item.title}
              className="rounded-[28px] border border-white/10 bg-white/[0.04] p-6 md:p-7"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.06] text-[#98dea3]">
                {item.icon}
              </div>
              <h3 className="mt-5 text-xl font-semibold">{item.title}</h3>
              <p className="mt-3 leading-relaxed text-white/72">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-y border-white/10 bg-white/[0.03]">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="text-sm uppercase tracking-[0.22em] text-[#a783a6]">
                {t("home.howItWorksBadge")}
              </p>
              <h2 className="mt-3 text-3xl font-bold leading-tight md:text-5xl">
                {t("home.howItWorksTitle")}
              </h2>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/72">
                {t("home.howItWorksSubtitle")}
              </p>
            </div>

            <div className="space-y-4">
              {steps.map((step) => (
                <div
                  key={step.num}
                  className="rounded-[28px] border border-white/10 bg-[#2c3140] p-6 md:p-7"
                >
                  <div className="flex items-start gap-4">
                    <div className="min-w-[60px] rounded-2xl bg-[#7269e3]/14 px-3 py-2 text-center text-sm font-semibold text-[#c9c4ff]">
                      {step.num}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{step.title}</h3>
                      <p className="mt-2 leading-relaxed text-white/72">{step.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOR BOTH SIDES */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="max-w-3xl">
          <p className="text-sm uppercase tracking-[0.22em] text-[#98dea3]">
            {t("home.forWhomBadge")}
          </p>
          <h2 className="mt-3 text-3xl font-bold md:text-5xl">
            {t("home.forWhomTitle")}
          </h2>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {benefits.map((item) => (
            <div
              key={item.title}
              className="rounded-[32px] border border-white/10 bg-white/[0.04] p-7 md:p-8"
            >
              <div
                className="inline-flex rounded-full px-4 py-2 text-sm font-medium"
                style={{
                  backgroundColor: `${item.accent}1A`,
                  color: item.accent,
                  border: `1px solid ${item.accent}33`,
                }}
              >
                {item.title}
              </div>
              <p className="mt-5 text-lg leading-relaxed text-white/76">{item.text}</p>
              <div className="mt-8 h-1.5 w-24 rounded-full bg-white/8">
                <div className="h-1.5 rounded-full" style={{ width: "70%", backgroundColor: item.accent }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BRAND BLOCK */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6 md:pb-24">
        <div className="rounded-[36px] border border-white/10 bg-[#2b3040] p-8 md:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <p className="text-sm uppercase tracking-[0.22em] text-[#a783a6]">
                {t("home.philosophyBadge")}
              </p>
              <h2 className="mt-3 text-3xl font-bold leading-tight md:text-5xl">
                {t("home.philosophyTitle")}
              </h2>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/72">
                {t("home.philosophyText")}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {philosophyItems.map((item, i) => (
                <div
                  key={i}
                  className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5"
                >
                  <div className="text-sm text-white/45">0{i + 1}</div>
                  <div className="mt-3 text-lg font-semibold">{item}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-16 md:px-6 md:pb-24">
        <div className="mx-auto max-w-7xl rounded-[36px] border border-[#7269e3]/20 bg-[#7269e3]/10 p-8 text-center md:p-14">
          <p className="text-sm uppercase tracking-[0.22em] text-[#c9c4ff]">
            {t("home.ctaBadge")}
          </p>
          <h2 className="mt-4 font-unbounded text-3xl font-extrabold leading-tight md:text-5xl">
            {t("home.ctaTitle")}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/80">
            {t("home.ctaSubtitle")}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/signup"
              className="rounded-2xl bg-[#98dea3] px-6 py-3 font-semibold text-[#202632] transition hover:opacity-90"
            >
              {t("home.ctaSignup")}
            </Link>
            <Link
              to="/search"
              className="rounded-2xl border border-white/12 bg-white/[0.05] px-6 py-3 font-semibold text-white transition hover:bg-white/[0.08]"
            >
              {t("home.ctaBrowse")}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20l-4.2-4.2" />
    </svg>
  );
}

function RequestIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M4 6.5A2.5 2.5 0 016.5 4h11A2.5 2.5 0 0120 6.5v7A2.5 2.5 0 0117.5 16H9l-5 4v-13.5z" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M21 15a2 2 0 01-2 2H8l-5 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      <path d="M8 9h8M8 13h5" />
    </svg>
  );
}
